import React, { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import ForgotPassword from "./Components/ForgotPassword";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { FaSpinner } from "react-icons/fa"; // Import spinner icon
import { Capacitor } from "@capacitor/core";
import { StatusBar } from "@capacitor/status-bar";
import { auth } from "./Configuration/firebase";
import Home from "./Components/Home";
import Main from "./Components/Main";
import SignIn from "./Components/SignIn";
import SignUp from "./Components/SignUp";
import AddItem from "./Components/AddItem";
import EditItem from "./Components/EditItem";
import ShareAddress from "./Components/ShareAddress";
import { ItemProvider } from "./Components/ItemContext";
import { ThemeProvider } from "./Components/ThemeContext";
import { NotificationsProvider } from "./Components/NotificationsContext";
import UpdateUser from "./Components/UpdateUser";
import ManageCategories from "./Components/ManageCategories";
import ContactUs from "./Components/ContactUs";
import VerifyPhone from "./Components/VerifyPhone";
import RequirePhone from "./Components/RequirePhone";
import StartEvent from "./Components/StartEvent";
import JoinEvent from "./Components/JoinEvent";
import EventDetails from "./Components/EventDetails";
import EventDeepLink from "./Components/EventDeepLink";
import DeepLinkHandler from "./Components/DeepLinkHandler";
import Safety from "./Components/Safety";
import TrustedContacts from "./Components/TrustedContacts";

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // True immersive/fullscreen: hide the status bar entirely so app
    // content covers the whole screen. No clock/battery/wifi icons
    // visible while using the app - a deliberate tradeoff.
    if (Capacitor.isNativePlatform()) {
      StatusBar.hide().catch((error) => {
        console.error("Error hiding status bar:", error);
      });
    }
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "top",
          height: "100vh",
        }}
      >
        <FaSpinner size={50} className="spin" />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <ItemProvider>
        <NotificationsProvider>
        <Router>
          <DeepLinkHandler />
          <Routes>
            <Route
              path="/"
              element={user ? <Home /> : <Navigate to="/login" />}
            />
            <Route
              path="/addresses"
              element={user ? <Main /> : <Navigate to="/login" />}
            />
            <Route
              path="/login"
              element={
                user ? (
                  user.emailVerified ? (
                    <Navigate to="/" />
                  ) : (
                    <SignIn />
                  )
                ) : (
                  <SignIn />
                )
              }
            />
            <Route path="/signup" element={<SignUp />} />
            <Route
              path="/add"
              element={user ? <AddItem /> : <Navigate to="/login" />}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/edit-item"
              element={user ? <EditItem /> : <Navigate to="/login" />}
            />
            <Route
              path="/update-user"
              element={user ? <UpdateUser /> : <Navigate to="/login" />}
            ></Route>
            <Route
              path="/manage-categories"
              element={user ? <ManageCategories /> : <Navigate to="/login" />}
            />
            <Route
              path="/share-address"
              element={user ? <ShareAddress /> : <Navigate to="/login" />}
            ></Route>
            <Route
              path="/contact-us"
              element={user ? <ContactUs /> : <Navigate to="/login" />}
            ></Route>
            <Route
              path="/verify-phone"
              element={user ? <VerifyPhone /> : <Navigate to="/login" />}
            />
            {/* Events never send SMS, so they don't require phone
                verification - just an authenticated user. */}
            <Route
              path="/start-event"
              element={user ? <StartEvent /> : <Navigate to="/login" />}
            />
            <Route
              path="/join-event"
              element={user ? <JoinEvent /> : <Navigate to="/login" />}
            />
            <Route
              path="/event/:code"
              element={user ? <EventDetails /> : <Navigate to="/login" />}
            />
            {/* Deep-link landing (Universal Link / App Link): join + open. */}
            <Route
              path="/e/:code"
              element={user ? <EventDeepLink /> : <Navigate to="/login" />}
            />
            <Route
              path="/safety"
              element={
                user ? (
                  <RequirePhone>
                    <Safety />
                  </RequirePhone>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/trusted-contacts"
              element={
                user ? (
                  <RequirePhone>
                    <TrustedContacts />
                  </RequirePhone>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          </Routes>
        </Router>
        </NotificationsProvider>
      </ItemProvider>
    </ThemeProvider>
  );
};

export default App;
